import { Avatar, Box, CircularProgress, Dialog, Divider, Fab, IconButton, List, ListItem, Paper, Stack, styled, TextField, Typography, Zoom } from "@mui/material"
import SendIcon from '@mui/icons-material/Send';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import { useEffect, useRef, useState } from "react";
import { getChat } from "src/services/chatbot.service";
import Markdown from "markdown-to-jsx";
import { Link } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { id } from "date-fns/locale";

const CustomDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-container": {
    "justify-content": "flex-end",
    "align-items": "flex-end",
  },
}));

const Index = () => {
  const [open, setOpen] = useState<boolean>(false);
  const [messages, setMessages] = useState<{ text: string; sender: string; time: Date }[]>([
    { text: "Halo! Ada yang bisa saya bantu?", sender: "bot", time: new Date() }
  ]);
  const [input, setInput] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [sessionId, setSessionId] = useState<string>("");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLDivElement>(null);  
  const userMessageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const legacySession = localStorage.getItem("chat_session_id");
  
    if (legacySession) {
      // Beri tahu pengguna tentang perubahan
      setMessages(prev => [
        ...prev,
        { 
          text: "Catatan: Riwayat chat sebelumnya tidak tersedia setelah pembaruan ini", 
          sender: "bot", 
          time: new Date() 
        }
      ]);
    }
    
    localStorage.removeItem("chat_session_id");
    const newSessionId = "session-" + Math.random().toString(36).substring(2, 9);
    setSessionId(newSessionId);
    setMessages([{ text: "Halo! Ada yang bisa saya bantu?", sender: "bot", time: new Date() }]);
  }, []);

  useEffect(() => {
    setTimeout(() => {
      userMessageRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
    const timer = setInterval(() => {
      // Force re-render for relative time updates
      setMessages(prev => [...prev]);
    }, 60000);
    
    return () => clearInterval(timer);
  }, [messages]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    // Add user message
    const userMessage = { text: input, sender: "user", time: new Date() };
    setMessages(prev => [...prev, userMessage]);
    setInput("");
    setLoading(true);

    setTimeout(() => {
      userMessageRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
    
    try {
      const response = await getChat({ session_id: sessionId, message: input });
      const botMessage = { 
        text: response?.data?.response || "Maaf, saya tidak mengerti", 
        sender: "bot", 
        time: new Date() 
      };
      setMessages(prev => [...prev, botMessage]);
    } catch (error) {
      const errorMessage = { 
        text: "Maaf, terjadi kesalahan. Silakan coba lagi.", 
        sender: "bot", 
        time: new Date() 
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
      setTimeout(() => {
        userMessageRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 50);
    }
  };

  return (
    <>
      <Zoom in={!open} style={{ transitionDelay: "500ms" }}>
        <Fab
          color="primary"
          aria-label="add"
          sx={{
            display: open ? "none" : "block",
            backgroundColor: "primary.main",
            position: "fixed",
            right: { xs: 30, sm: 24, md: 32, lg: 40 },
            bottom: { xs: 80, lg: 40 },
            width: { xs: 50, lg: 60 },
            height: { xs: 50, lg: 60 },
            p: 1
          }}
          onClick={() => setOpen(true)}
        >
          <img style={{ width: "100%" }} src="/ai.png" alt="support" />
        </Fab>
			</Zoom>

      {/* Popup Chat */}
      <CustomDialog 
        open={open} 
        onClose={() => setOpen(false)}
        sx={{
          "& .MuiDialog-paper": {
            margin: 0,
            maxHeight: 'calc(100% - 64px)',
            height: 'auto',
            position: 'fixed',
            maxWidth: '100%',
            bottom: 0,
            m: 2,
            borderRadius: 1
          }
        }}
        disableEnforceFocus
      >
        <Paper
          sx={{
            width: { xs: "100%", sm: 450 },
            maxWidth: { xs: "100%", sm: 450 },
            height: { xs: "80vh", sm: 500 },
            maxHeight: { xs: "80vh", sm: 500 },
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Header */}
          <Box sx={{ 
            bgcolor: "primary.main", 
            color: "white",
            p: 2, 
            display: "flex", 
            alignItems: "center", 
            borderRadius: "8px 8px 0px 0px"
          }}>
            <Avatar sx={{ bgcolor: "white", color: "primary.main", mr: 2 }}>
              <img
                src="/android-chrome-192x192.png"
                alt="Brofesional logo"
                width="50px"
              />
            </Avatar>
            <Typography variant="h6">Brodi AI</Typography>
          </Box>

          {/* Chat History */}
          <Box sx={{ 
            overflowY: "auto", 
            flex: 1,
            bgcolor: "background.default",
          }}>
            <List>
              {messages.map((msg, index) => (
                <Box key={index} ref={msg.sender === "user" ? userMessageRef : null}>
                  <ListItem 
                    sx={{ 
                      justifyContent: msg.sender === "user" ? "flex-end" : "flex-start",
                      alignItems: "flex-start",
                      mb: 1
                    }}
                  >
                    {msg.sender === "bot" && (
                      <Avatar sx={{ bgcolor: "white", color: "white", mr: 1 }}>
                        <img
                          src="/android-chrome-192x192.png"
                          alt="Brofesional logo"
                          width="50px"
                        />
                      </Avatar>
                    )}
                    <Paper
                      sx={{
                        p: 1.5,
                        maxWidth: "70%",
                        bgcolor: msg.sender === "user" ? "primary.light" : "grey.100",
                        color: msg.sender === "user" ? "white" : "text.primary"
                      }}
                    >
                      <Markdown style={{ whiteSpace: "pre-wrap", wordBreak: "break-word"}}>{msg.text}</Markdown>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          display: "block", 
                          textAlign: "right",
                          textTransform: "lowercase",
                          color: msg.sender === "user" ? "grey.300" : "grey.500"
                        }}
                      >
                        {formatDistanceToNow(new Date(msg.time), {
                          addSuffix: true,
                          locale: id
                        })}
                      </Typography>
                    </Paper>
                    {msg.sender === "user" && (
                      <Avatar sx={{ bgcolor: "grey.500", ml: 1 }}>
                        <AccountCircleIcon fontSize="small" />
                      </Avatar>
                    )}
                  </ListItem>
                  <Divider variant="inset" sx={{ my: 1 }} />
                </Box>
              ))}
              <div ref={messagesEndRef} />
            </List>
          </Box>

          {/* Input Form */}
          <Box 
            ref={inputRef}
            sx={{ 
              p: 2, 
              bgcolor: "background.paper",
              position: "sticky",
              bottom: 0,
              borderRadius: "0px 0px 8px 8px"
            }}
          >
            <form onSubmit={handleSubmit}>
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                  fullWidth
                  size="small"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Ketik pesan..."
                  sx={{ mt: 0 }}
                  inputRef={inputRef}
                />
                <IconButton 
                  type="submit" 
                  color="primary" 
                  disabled={!input.trim() || loading}
                >
                  {loading ? <CircularProgress size={24} /> : <SendIcon />}
                </IconButton>
              </Stack>
            </form>
          </Box>
        </Paper>
      </CustomDialog>
    </>
  )
}

export default Index